// ============================================
// 2026-10-15（P0-④ 鸟瞰相机）：鸟瞰相机属性面板（左栏「功能」区，场景树 cam:birdseye 节点进入）
// 分组（对齐山海鲸，P0 架构 §3.3）：基础设置 / 视角设置 / 相机控制（P1）
// ============================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Switch, Slider, InputNumber, Input, Segmented, Select, Button, Space, Typography, Empty, message } from 'antd';
import { CameraOutlined, SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { cameraControlsRegistry, type CameraViewSnapshot } from '../../stores/cameraControlsStore';

const { Text } = Typography;

/** 问标行（label + tooltip=说明+使用案例；同 SceneSettingsPanel 约定） */
function Field({ label, tooltip, children }: { label: string; tooltip: string; children: React.ReactNode }) {
  return (
    <Form.Item label={label} tooltip={tooltip} style={{ marginBottom: 12 }}>
      {children}
    </Form.Item>
  );
}

const deg = (r: number) => ((r * 180) / Math.PI).toFixed(0);
const rad = (d: number) => (d * Math.PI) / 180;

export default function BirdsEyeCameraPanel() {
  const birds = useSceneStore((s) => s.birdsEyeCamera);
  const setBirdsEyeCamera = useSceneStore((s) => s.setBirdsEyeCamera);
  const setMainCamera = useSceneStore((s) => s.setMainCamera);
  // 2026-10-16（P1-3）：主相机设置（editor/birdsEye/path）+ 漫游相机列表
  const mainCamera = useSceneStore((s) => s.mainCamera);
  const pathCameras = useSceneStore((s) => s.pathCameras);
  const [presetName, setPresetName] = useState('');

  // 当前视角（球坐标）只读展示：2Hz 刷新（面板打开期间）
  const [viewTick, setViewTick] = useState(0);
  const currentView = useMemo<CameraViewSnapshot | null>(() => {
    void viewTick;
    return cameraControlsRegistry.getCameraView?.() ?? null;
  }, [viewTick, birds]);

  useEffect(() => {
    const t = setInterval(() => setViewTick((v) => v + 1), 500);
    return () => clearInterval(t);
  }, []);

  const toggleEnabled = useCallback((enabled: boolean) => {
    setBirdsEyeCamera({ enabled });
    setMainCamera({ type: enabled ? 'birdsEye' : 'editor' });
    if (enabled && !birds.initialView) {
      // 首次启用：写入默认鸟瞰初始视角（正上方俯视，半径=Z 轴距离）——持久化，保证「初始视角」可保存可恢复
      setBirdsEyeCamera({ initialView: { alpha: 0, beta: 0.6, radius: Math.max(1, birds.zDistance), target: [0, 0, 0] } });
    }
  }, [setBirdsEyeCamera, setMainCamera, birds.initialView, birds.zDistance]);

  const saveInitialView = useCallback(() => {
    const cur = cameraControlsRegistry.getCameraView?.();
    if (!cur) { message.warning('当前无法读取视角（鸟瞰相机未激活）'); return; }
    setBirdsEyeCamera({ initialView: cur });
    message.success('已保存为初始视角');
  }, [setBirdsEyeCamera]);

  const applyView = useCallback((view: CameraViewSnapshot) => {
    cameraControlsRegistry.applyCameraView?.(view);
  }, []);

  const addPreset = useCallback(() => {
    const cur = cameraControlsRegistry.getCameraView?.();
    if (!cur) { message.warning('当前无法读取视角'); return; }
    const name = presetName.trim() || `预设 ${birds.presets.length + 1}`;
    setBirdsEyeCamera({ presets: [...birds.presets, { id: `pv-${Date.now().toString(36)}`, name, view: cur }] });
    setPresetName('');
    message.success(`已新增预设「${name}」`);
  }, [birds.presets, presetName, setBirdsEyeCamera]);

  return (
    <div style={{ padding: '8px 10px 16px' }} data-testid="bec-panel">
      <Text strong style={{ fontSize: 13 }}><CameraOutlined /> 鸟瞰相机</Text>
      <Form layout="vertical" size="small" style={{ marginTop: 8 }}>
        <Field label="启用鸟瞰" tooltip={'开启后主相机切换到鸟瞰视角（正交/透视可选）；关闭回到编辑器相机。\n使用案例：园区大屏用固定俯视鸟瞰展示整体布局。'}>
          <Switch size="small" data-testid="bec-enabled" checked={birds.enabled} onChange={toggleEnabled} />
        </Field>
        <Field label="名称" tooltip={'相机名称（显示在场景树）。\n使用案例：多个方案切换时命名为「俯视总览」。'}>
          <Input size="small" data-testid="bec-name" value={birds.name} onChange={(e) => setBirdsEyeCamera({ name: e.target.value })} />
        </Field>
        <Field label="选择视图" tooltip={'透视=近大远小更自然；正交=无透视变形，适合平面化园区布局。\n使用案例：车间平面布局用正交。'}>
          <Segmented
            size="small"
            data-testid="bec-view-type"
            value={birds.viewType}
            onChange={(v) => setBirdsEyeCamera({ viewType: v as 'perspective' | 'orthographic' })}
            options={[{ value: 'perspective', label: '透视' }, { value: 'orthographic', label: '正交' }]}
          />
        </Field>
        <Field label="Z 轴距离 (m)" tooltip={'相机初始高度（未保存初始视角时生效）。\n使用案例：厂区 200m 范围用 150~250。'}>
          <InputNumber
            size="small"
            data-testid="bec-z-distance"
            style={{ width: '100%' }}
            min={1}
            max={5000}
            step={10}
            value={birds.zDistance}
            onChange={(v) => setBirdsEyeCamera({ zDistance: Number(v) || 50 })}
          />
        </Field>
        <Field label="屏幕中点偏移 (X / Y)" tooltip={'把画面中心平移出场景中心（比例值，1 = 一个相机距离）。\n使用案例：侧栏遮挡画面左侧时把中心右移 0.1。'}>
          <Space size={6}>
            <InputNumber
              size="small"
              data-testid="bec-offset-x"
              style={{ width: 86 }}
              min={-1}
              max={1}
              step={0.05}
              value={birds.screenOffset[0]}
              onChange={(v) => setBirdsEyeCamera({ screenOffset: [Number(v) || 0, birds.screenOffset[1]] })}
            />
            <InputNumber
              size="small"
              data-testid="bec-offset-y"
              style={{ width: 86 }}
              min={-1}
              max={1}
              step={0.05}
              value={birds.screenOffset[1]}
              onChange={(v) => setBirdsEyeCamera({ screenOffset: [birds.screenOffset[0], Number(v) || 0] })}
            />
          </Space>
        </Field>
        <Field label="缩放限制 (最近 / 最远 m)" tooltip={'滚轮缩放的距离上下限。\n使用案例：防止缩进地面或拉到看不见场景。'}>
          <Space size={6}>
            <InputNumber
              size="small"
              data-testid="bec-zoom-min"
              style={{ width: 86 }}
              min={1}
              max={10000}
              step={5}
              value={birds.zoomMin}
              onChange={(v) => setBirdsEyeCamera({ zoomMin: Number(v) || 5 })}
            />
            <InputNumber
              size="small"
              data-testid="bec-zoom-max"
              style={{ width: 86 }}
              min={2}
              max={20000}
              step={10}
              value={birds.zoomMax}
              onChange={(v) => setBirdsEyeCamera({ zoomMax: Number(v) || 200 })}
            />
          </Space>
        </Field>
        <Field label="垂直角度限制 (最小 / 最大 °)" tooltip={'相机与垂直方向的夹角范围（0=正上方俯视，90=水平）。\n使用案例：1~85 防止翻转到地平线下。'}>
          <Space size={6}>
            <InputNumber
              size="small"
              data-testid="bec-polar-min"
              style={{ width: 86 }}
              min={0}
              max={89}
              step={1}
              value={birds.polarMin}
              onChange={(v) => setBirdsEyeCamera({ polarMin: Number(v) || 0 })}
            />
            <InputNumber
              size="small"
              data-testid="bec-polar-max"
              style={{ width: 86 }}
              min={1}
              max={90}
              step={1}
              value={birds.polarMax}
              onChange={(v) => setBirdsEyeCamera({ polarMax: Number(v) || 85 })}
            />
          </Space>
        </Field>
        <Field label={`视角大小 (fov ${birds.fov}°)`} tooltip={'透视相机的视野角度（仅透视生效）。\n使用案例：60° 更广，40° 更聚焦。'}>
          <Slider data-testid="bec-fov" min={10} max={100} step={1} value={birds.fov} onChange={(v) => setBirdsEyeCamera({ fov: v })} />
        </Field>
        <Field label={`正交缩放 (半高 ${birds.orthoZoom} m)`} tooltip={'正交相机可视范围半高（仅正交生效）。\n使用案例：园区 300m 宽用 150。'}>
          <Slider data-testid="bec-ortho-zoom" min={5} max={1000} step={5} value={birds.orthoZoom} onChange={(v) => setBirdsEyeCamera({ orthoZoom: v })} />
        </Field>
        <Field label="自动旋转" tooltip={'相机绕目标缓慢自转（演示模式）。\n使用案例：展厅无人操作时缓慢旋转展示。'}>
          <Space size={8}>
            <Switch size="small" data-testid="bec-auto-rotate" checked={birds.autoRotate} onChange={(v) => setBirdsEyeCamera({ autoRotate: v })} />
            <Slider
              style={{ width: 120 }}
              min={0.5}
              max={10}
              step={0.5}
              value={birds.autoRotateSpeed}
              disabled={!birds.autoRotate}
              onChange={(v) => setBirdsEyeCamera({ autoRotateSpeed: v })}
            />
          </Space>
        </Field>
      </Form>

      <Text strong style={{ fontSize: 13 }}>视角设置</Text>
      <div style={{ margin: '8px 0' }}>
        <Text data-testid="bec-current-view" style={{ fontSize: 11, color: '#888', display: 'block', lineHeight: 1.7 }}>
          {currentView
            ? <>方位 {deg(currentView.alpha)}° · 俯仰 {deg(currentView.beta)}° · 距离 {currentView.radius.toFixed(1)} m<br />
              目标 ({currentView.target.map((v) => v.toFixed(1)).join(', ')})</>
            : '当前视角不可读（鸟瞰相机未激活）'}
        </Text>
        <Space size={6} style={{ marginTop: 6 }}>
          <Button size="small" data-testid="bec-save-initial" icon={<SaveOutlined />} onClick={saveInitialView}>保存为初始视角</Button>
        </Space>
      </div>
      <div>
        <Space size={6} style={{ marginBottom: 6 }}>
          <Input
            size="small"
            data-testid="bec-preset-name"
            placeholder="预设名称"
            style={{ width: 120 }}
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
          />
          <Button size="small" data-testid="bec-preset-add" icon={<PlusOutlined />} onClick={addPreset}>新增预设</Button>
        </Space>
        {birds.presets.length === 0 ? (
          <Empty description="暂无预设" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          birds.presets.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }} data-testid={`bec-preset-${p.id}`}>
              <Text style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</Text>
              <Button size="small" type="link" data-testid={`bec-preset-apply-${p.id}`} onClick={() => applyView(p.view)}>应用</Button>
              <Button
                size="small"
                type="text"
                danger
                data-testid={`bec-preset-del-${p.id}`}
                icon={<DeleteOutlined />}
                onClick={() => setBirdsEyeCamera({ presets: birds.presets.filter((x) => x.id !== p.id) })}
              />
            </div>
          ))
        )}
      </div>

      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginTop: 12 }}>
        相机控制
      </Text>
      {/* 2026-10-16（P1-3）：主相机设置接线（editor/birdsEye/path → scene.config.mainCamera；打开场景即应用） */}
      <Form layout="vertical" size="small" style={{ marginTop: 4 }}>
        <Field
          label="主相机"
          tooltip={'设定场景打开时的主相机：编辑器相机（自由视角）/ 鸟瞰相机 / 指定漫游相机（落到该相机最后一帧位姿）。\n使用案例：交付大屏固定机位用漫游相机。'}
        >
          <Select
            size="small"
            data-testid="bec-main-camera"
            style={{ width: '100%' }}
            value={mainCamera.type}
            onChange={(v) => {
              if (v === 'birdsEye') {
                setBirdsEyeCamera({ enabled: true });
                setMainCamera({ type: 'birdsEye' });
              } else if (v === 'path') {
                setMainCamera({ type: 'path', pathCameraId: pathCameras[0]?.id });
              } else {
                setMainCamera({ type: 'editor' });
              }
            }}
            options={[
              { value: 'editor', label: '编辑器相机' },
              { value: 'birdsEye', label: '鸟瞰相机' },
              { value: 'path', label: '漫游相机', disabled: pathCameras.length === 0 },
            ]}
          />
        </Field>
        {mainCamera.type === 'path' && (
          <Field label="漫游相机" tooltip={'选择用哪台漫游相机作为主相机（取最后一帧位姿；注视点=视线方向 10m）。\n使用案例：入口/出口机位切换。'}>
            <Select
              size="small"
              data-testid="bec-path-camera"
              style={{ width: '100%' }}
              value={mainCamera.pathCameraId}
              onChange={(v) => setMainCamera({ type: 'path', pathCameraId: v })}
              options={pathCameras.map((c, i) => ({ value: c.id, label: c.name || `漫游相机 ${i + 1}` }))}
            />
          </Field>
        )}
      </Form>
    </div>
  );
}
